import React, { useState, createContext, useContext } from 'react'
import { Button } from '~/lib/ui/button'
import { useMessaging } from './MessagingProvider'
import CoachMessage from './CoachMessage'
import ThreadReply from './ThreadReply'
import { apiGet, apiDelete } from '~/util/api'
import { useQuery, useMutation } from '@tanstack/react-query'
import { ScrollArea } from '~/lib/ui/scroll-area'
import YnsIcon from '../icons/YnsIcon'
import useTranslation from '~/components/shared/TranslationProvider'
import { cn } from '~/lib/utils'

const ReplyContext = createContext()

const CoachMessageThread = () => {
  const { t } = useTranslation('CoachMessageThread')
  const { currentThreadId, isMobile, queryClient } = useMessaging()
  const [isDeleted, setIsDeleted] = useState(false)

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`${location.pathname}/${currentThreadId}.json`),
    mutationKey: ['deleteThread', currentThreadId],
    onSuccess: (res) => {
      const latestId = res?.data?.latest_thread_id || null
      window.location.href = `#${latestId}`
      queryClient.invalidateQueries(['messageThreads'])
      setIsDeleted(false)
    },
  })

  const {
    error,
    data: messageThread,
    isPending,
  } = useQuery({
    queryKey: ['messageThread', currentThreadId],
    queryFn: () =>
      apiGet(`${location.pathname}/${currentThreadId}.json`).then((res) => {
        return res.data
      }),
    enabled: !(isDeleted || !currentThreadId),
  })

  if (!currentThreadId) return <div className="h-full"></div>

  if (isPending) return <div className="h-full">{t('loading')}</div>

  if (error) {
    console.error('Error fetching message thread:', error)
    return 'An error has occurred'
  }

  const {
    coach_messages: messages,
    coach,
    candidate_name: candidateName,
    subject,
  } = messageThread

  const ViewComp = isMobile ? MobileView : DesktopView

  const messageProps = { messages, coach, candidateName }

  const handleDeleteClick = () => {
    setIsDeleted(true)
    deleteMutation.mutate()
  }

  return (
    <div className="mt-8 flex h-full flex-col overflow-y-hidden rounded-3xl border border-silver bg-white p-4">
      <div className="relative -mx-4 -mt-4 flex items-center justify-between border-b bg-intoo-green-coachcta px-6 py-4">
        <div className="font-roboto-slab text-white md:text-2xl">
          {t('subject_line')} {subject}
        </div>
        <YnsIcon
          name="trash"
          className="mr-2 h-6 w-6 cursor-pointer text-white"
          onClick={handleDeleteClick}
          aria-label={t('delete')}
        />
      </div>
      <ReplyProvider messageThread={messageThread}>
        <ViewComp subject={subject}>
          <CoachMessages {...messageProps} />
        </ViewComp>
      </ReplyProvider>
    </div>
  )
}

const ReplyProvider = ({ messageThread, children }) => {
  const { coach, userIsCoach } = useMessaging()
  const { candidate_name: candidateName } = messageThread || {}
  const coachName = [coach?.first_name, coach?.last_name].join(' ')
  const recipientName = userIsCoach ? candidateName : coachName
  const {
    coach_deleted_at: coachDeletedAt,
    candidate_deleted_at: candidateDeletedAt,
  } = messageThread || {}
  const isDeletedByOther = !!(userIsCoach ? candidateDeletedAt : coachDeletedAt)

  return (
    <ReplyContext.Provider value={{ recipientName, isDeletedByOther }}>
      {children}
    </ReplyContext.Provider>
  )
}

const MobileView = ({ children }) => {
  return (
    <div className="flex flex-col py-2">
      <ThreadReplyToggle />
      <div>{children}</div>
    </div>
  )
}

const DesktopView = ({ children }) => {
  return (
    <ScrollArea className="pl-2 pr-3 pt-2">
      <ThreadReplyToggle className="float-right w-1/3" />
      <div className="mt-8">{children}</div>
    </ScrollArea>
  )
}

const ThreadReplyToggle = ({ className }) => {
  const [replying, setReplying] = useState(false)
  const { t } = useTranslation('CoachMessageThread')
  const { recipientName: name, isDeletedByOther } = useContext(ReplyContext)
  if (isDeletedByOther)
    return (
      <div className="mb-4 mt-2 border-b border-silver pb-4">
        <div className="text-xl font-medium">
          {t('archived_thread', { name })}
        </div>
        <div className="text-sm">{t('archived_thread_detail')}</div>
      </div>
    )

  if (replying)
    return (
      <div className="mb-4 border-b border-silver pb-4">
        <h2>{t('replying_to', { name })}</h2>
        <ThreadReply setReplying={setReplying} />
      </div>
    )

  return (
    <Button
      onClick={() => setReplying(true)}
      variant="coachcta"
      className={cn('mb-4 w-full border-b border-silver', className)}
    >
      {t('reply_to', { name })}
    </Button>
  )
}

const CoachMessages = ({ messages, coach, candidateName }) => {
  return (
    <>
      {messages?.reverse()?.map((message, _index) => (
        <CoachMessage
          key={message.id}
          message={message}
          coach={coach}
          candidateName={candidateName}
        />
      ))}
    </>
  )
}

export default CoachMessageThread
